// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CaseDetail/components/ActivityTimeline.tsx

import type { NavigatorCaseActivity } from "../types";
import { formatDateTime, humanizeKey } from "../intakeUtils";

type ActivityTimelineProps = {
  activity: NavigatorCaseActivity[];
};

const ACTIVITY_TITLES: Record<string, string> = {
  CaseCreated: "Case created",
  Assigned: "Assigned",
  WorkflowUpdate: "Patient details updated",
  Intake: "Intake completed",
  Match: "Match run",
  ForwardedToProvider: "Forwarded to provider",
  Closed: "Case closed",
};

const REDUNDANT_ACTIVITY_OUTCOMES: Record<string, string> = {
  ForwardedToProvider: "ForwardedToProvider",
  WorkflowUpdate: "PatientDetailsUpdated",
};

function activityTitle(activityType: string) {
  return ACTIVITY_TITLES[activityType] || humanizeKey(activityType);
}

function shouldShowOutcome(activityType: string, outcomeCode: string | null) {
  if (!outcomeCode) return false;
  return REDUNDANT_ACTIVITY_OUTCOMES[activityType] !== outcomeCode;
}

function activityMeta(item: NavigatorCaseActivity) {
  const name = item.createdByName?.trim() || "";
  const role = item.createdByRole?.trim() || "";

  return [
    name || null,
    role ? humanizeKey(role) : null,
    formatDateTime(item.createdAt),
  ]
    .filter(Boolean)
    .join(" · ");
}

export default function ActivityTimeline({ activity }: ActivityTimelineProps) {
  return (
    <section className="cn-case-detail-card cn-case-detail-wide">
      <div className="cn-case-detail-card-header">
        <h2>Activity timeline</h2>
      </div>

      {activity.length ? (
        <ol className="cn-case-detail-activity-list">
          {activity.map((item) => (
            <li
              className="cn-case-detail-activity"
              key={item.navigatorCaseActivityId}
            >
              <div className="cn-case-detail-activity-rail" aria-hidden="true">
                <span className="cn-case-detail-activity-dot" />
              </div>
              <div className="cn-case-detail-activity-body">
                <div className="cn-case-detail-activity-title">
                  <strong>{activityTitle(item.activityType)}</strong>
                  {shouldShowOutcome(item.activityType, item.outcomeCode) ? (
                    <span>{humanizeKey(item.outcomeCode || "")}</span>
                  ) : null}
                  {item.isSystemGenerated === true ? <em>System</em> : null}
                </div>

                {item.note ? <p>{item.note}</p> : null}

                <footer>{activityMeta(item)}</footer>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="cn-case-detail-muted">
          No activity has been recorded yet.
        </p>
      )}
    </section>
  );
}
